import { Link } from "react-router-dom";
import { MapPin, ChevronRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PageHero, SectionHead, CtaBand } from "@/components/Blocks";
import { CITIES, COUNTIES, IMAGES } from "@/data/contentEngine";

const CitiesIndex = () => (
  <>
    <Seo
      title="Security Guards by City | Areas We Cover in Northern Ireland | Xtreme Protection"
      description="SIA-licensed security guards in Belfast, Derry/Londonderry, Lisburn, Newry, Armagh, Bangor, Craigavon, Newtownabbey, Ballymena, Coleraine, Omagh, Enniskillen & all NI towns."
      path="/cities"
    />
    <PageHero
      eyebrow="Areas We Cover"
      title="Security guards across Northern Ireland"
      sub="Locally recruited, SIA-licensed officers in 43 cities, towns and villages — from Belfast's Titanic Quarter to the lakelands of Fermanagh."
      image={IMAGES.belfastArch}
      testId="cities-index-hero"
    />
    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-12">
      <SectionHead
        eyebrow="6 Counties"
        title="Browse by county"
        testId="counties-band-heading"
      />
      <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {COUNTIES.map((c) => (
          <Link
            key={c.slug}
            to={`/counties/${c.slug}`}
            data-testid={`county-band-${c.slug}`}
            className="tactical-card group flex items-center justify-center rounded-md px-3 py-3 text-center font-display text-sm font-bold uppercase tracking-wide text-slate-200 transition-colors hover:text-amber-400"
          >
            {c.name}
          </Link>
        ))}
      </div>
      <div className="mt-14">
        <SectionHead
          eyebrow="43 Locations · 6 Counties"
          title="Choose your city, town or village"
          testId="cities-index-heading"
        />
      </div>
      <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {CITIES.map((c, i) => (
          <Reveal key={c.slug} delay={(i % 3) * 0.08}>
            <Link
              to={`/cities/${c.slug}`}
              data-testid={`cities-index-card-${c.slug}`}
              className="tactical-card group flex h-full flex-col rounded-md p-6"
            >
              <div className="flex items-center justify-between">
                <MapPin className="h-6 w-6 text-amber-500" />
                <ChevronRight className="h-4 w-4 text-slate-600 transition-all duration-300 group-hover:translate-x-1 group-hover:text-amber-500" />
              </div>
              <h2 className="mt-4 font-display text-xl font-bold uppercase tracking-wide text-slate-100">
                Security Guards {c.name}
              </h2>
              <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-amber-500/80">
                {c.county}
              </p>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400">
                {c.intro.split(".")[0]}.
              </p>
              <p className="mt-4 text-[11px] text-slate-500">
                Covering {c.hubs.slice(0, 3).join(" · ")}
              </p>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
    <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-12">
      <CtaBand
        title="Your area not listed?"
        sub="We cover every postcode in Northern Ireland — towns, villages and rural sites included. Call the control room to confirm cover for your location."
      />
    </section>
  </>
);

export default CitiesIndex;
